import React from "react"
import { Link } from "react-router-dom"
import { useEffect, useState, useCallback, useRef, useMemo } from "react"
import { useLocation } from "react-router-dom"
import { api } from "../api/client"
import { useAuth } from "../context/AuthContext"
import { useLanguage } from "../context/LanguageContext"
import UniverseScene from "../components/UniverseScene"

const SKILL_KEYS = ["reading", "writing", "listening", "speaking", "grammar"]

const SKILL_ROUTES = {
  reading: "/reading",
  writing: "/writing",
  listening: "/listening",
  speaking: "/speaking",
  grammar: "/grammar",
}

const SKILL_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
]

function skillRoute(name) {
  if (!name) return "/reading"
  return SKILL_ROUTES[name.toLowerCase()] || "/reading"
}

function skillLabel(name, t) {
  const key = (name || "").toLowerCase()
  return SKILL_ROUTES[key] ? t(key) : name
}

function localDateKey(date = new Date()) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function buildLast7Days(dailyActivity) {
  const map = Object.fromEntries((dailyActivity || []).map((d) => [d.date, d]))
  const days = []
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    d.setDate(d.getDate() - i)
    const key = localDateKey(d)
    const entry = map[key]
    days.push({
      date: key,
      label: d.toLocaleDateString(undefined, { weekday: "short" }),
      lessons_completed: entry?.lessons_completed || 0,
      lessons_started: entry?.lessons_started || 0,
      total_activities: entry?.total_activities || 0,
      vocabulary_reviewed: entry?.vocabulary_reviewed || 0,
      vocabulary_saved: entry?.vocabulary_saved || 0,
    })
  }
  return days
}

function formatStudyTime(minutes) {
  const m = minutes || 0
  if (m < 60) return { value: m, unit: "min" }
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem > 0 ? { value: `${h}h ${rem}m`, unit: "" } : { value: h, unit: "h" }
}

function resolveFocusSkill(focusArea, skills) {
  const area = (focusArea || "balanced").toLowerCase()
  if (area !== "balanced" && SKILL_ROUTES[area]) {
    const match = skills.find((s) => s.name.toLowerCase() === area)
    return match?.name || area
  }
  const withLessons = skills.filter((s) => s.total > 0)
  if (!withLessons.length) return null
  return [...withLessons].sort((a, b) => a.percentage - b.percentage)[0]?.name
}

function orderedSkillKeys(focusName) {
  const focus = (focusName || "").toLowerCase()
  if (!SKILL_ROUTES[focus]) return [...SKILL_KEYS]
  return [focus, ...SKILL_KEYS.filter((k) => k !== focus)]
}

function RingGauge({ value, max = 100, size = 96, stroke = 7, label, display }) {
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const pct = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0
  const offset = circumference * (1 - pct)

  return (
    <div className="dashboard-ring-gauge">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="dashboard-ring-svg"
        role="img"
        aria-label={`${label}: ${display ?? value}`}
      >
        <circle
          className="dashboard-ring-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="dashboard-ring-progress"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="dashboard-ring-center">
        <span className="dashboard-ring-value tabular-nums">{display ?? value}</span>
      </div>
      <p className="dashboard-viz-card-label">{label}</p>
    </div>
  )
}

function WeekChart({ days, maxValue, emptyLabel, selectedDate, onSelectDay, t }) {
  const ticks = useMemo(() => {
    const top = Math.max(maxValue, 1)
    return [top, Math.ceil(top / 2), 0]
  }, [maxValue])

  const selectedDay = days.find((d) => d.date === selectedDate)

  return (
    <div className="dashboard-week-wrap">
      <div className="dashboard-week-viz">
        <div className="dashboard-week-y-axis" aria-hidden>
          {ticks.map((tick, index) => (
            <span key={`tick-${index}-${tick}`} className="tabular-nums">
              {tick}
            </span>
          ))}
        </div>
        <div className="dashboard-week-plot">
          <div className="dashboard-week-grid" aria-hidden>
            {ticks.map((tick, index) => (
              <div key={`grid-${index}-${tick}`} className="dashboard-week-grid-line" />
            ))}
          </div>
          <div className="dashboard-week-bars">
            {days.map((day) => {
              const value = day.lessons_completed + (day.vocabulary_reviewed || 0)
              const heightPct = maxValue > 0 ? (value / maxValue) * 100 : 0
              const isSelected = day.date === selectedDate
              return (
                <button
                  key={day.date}
                  type="button"
                  className={`dashboard-week-col ${isSelected ? "dashboard-week-col--active" : ""}`}
                  onClick={() => onSelectDay(day.date)}
                  aria-pressed={isSelected}
                  aria-label={`${day.label}: ${value}`}
                >
                  <div className="dashboard-week-bar-wrap">
                    <div
                      className="dashboard-week-bar"
                      style={{
                        height: `${Math.max(heightPct, value > 0 ? 6 : 0)}%`,
                      }}
                    />
                  </div>
                  <span className="dashboard-week-day">{day.label}</span>
                </button>
              )
            })}
          </div>
          {maxValue === 0 && <p className="dashboard-week-empty">{emptyLabel}</p>}
        </div>
      </div>
      {selectedDay && (
        <p className="dashboard-week-detail tabular-nums">
          <span>{selectedDay.label}</span>
          <span className="dashboard-week-detail-val">
            {selectedDay.lessons_completed} {t("lessons").toLowerCase()}
            {selectedDay.lessons_started > 0 && (
              <>
                {" · "}
                {selectedDay.lessons_started} {t("dashboardStartedShort")}
              </>
            )}
            {(selectedDay.vocabulary_reviewed > 0 || selectedDay.vocabulary_saved > 0) && (
              <>
                {" · "}
                {selectedDay.vocabulary_reviewed + selectedDay.vocabulary_saved} {t("vocabulary").toLowerCase()}
              </>
            )}
          </span>
        </p>
      )}
    </div>
  )
}

function SkillsChart({ skills, focusName, t }) {
  if (!skills.length) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">{t("noSkillsDataAvailable")}</p>
    )
  }

  const sorted = [...skills].sort((a, b) => {
    const fa = (focusName || "").toLowerCase()
    const aFocus = a.name.toLowerCase() === fa ? -1 : 0
    const bFocus = b.name.toLowerCase() === fa ? -1 : 0
    if (aFocus !== bFocus) return aFocus - bFocus
    return b.percentage - a.percentage
  })

  return (
    <div className="dashboard-skills-viz">
      {sorted.map((skill, index) => {
        const pct = Math.min(skill.percentage, 100)
        const isFocus = skill.name.toLowerCase() === (focusName || "").toLowerCase()
        return (
          <Link
            key={skill.name}
            to={skillRoute(skill.name)}
            className={`dashboard-skill-row ${isFocus ? "dashboard-skill-row--focus" : ""}`}
          >
            <div className="dashboard-skill-row-head">
              <span className="dashboard-skill-name">
                {skillLabel(skill.name, t)}
                {isFocus && (
                  <span className="dashboard-skill-focus-tag">{t("dashboardFocusTag")}</span>
                )}
              </span>
              <span className="dashboard-skill-pct tabular-nums">{Math.round(pct)}%</span>
            </div>
            <div className="dashboard-skill-track">
              <div
                className="dashboard-skill-fill"
                style={{
                  width: `${pct}%`,
                  background: SKILL_COLORS[index % SKILL_COLORS.length],
                }}
              />
            </div>
            <div className="dashboard-skill-row-foot">
              <span className="dashboard-skill-meta tabular-nums">
                {skill.completed}/{skill.total}
                {skill.vocabulary_count > 0
                  ? ` · ${skill.vocabulary_count} ${t("dashboardVocabWords")}`
                  : ""}
              </span>
              <span className="dashboard-skill-go">{t("dashboardPracticeNow")} →</span>
            </div>
          </Link>
        )
      })}
    </div>
  )
}

function StreakViz({ days, streak, t }) {
  return (
    <div className="dashboard-streak-viz">
      <div className="dashboard-streak-number tabular-nums">
        {streak}
        <span className="dashboard-streak-unit">{t("days")}</span>
      </div>
      <div className="dashboard-streak-dots" role="img" aria-label={t("learningStreak")}>
        {days.map((day) => {
          const learned = day.lessons_completed > 0 || day.vocabulary_reviewed > 0
          const level = learned ? 3 : day.total_activities > 0 ? 2 : 0
          return (
            <div
              key={day.date}
              className={`dashboard-streak-dot dashboard-streak-dot--${level}`}
              title={day.label}
            />
          )
        })}
      </div>
      <p className="dashboard-viz-card-label">{t("learningStreak")}</p>
    </div>
  )
}

function VocabLearnedViz({ learned, total, inProgress, t }) {
  const pct = total > 0 ? Math.min(Math.round((learned / total) * 100), 100) : 0
  return (
    <>
      <p className="dashboard-vocab-value tabular-nums">{learned}</p>
      <p className="dashboard-vocab-meta tabular-nums">
        {total > 0
          ? t("dashboardVocabOfSaved").replace("{total}", String(total))
          : t("dashboardVocabNoneSaved")}
      </p>
      <span className="dashboard-vocab-track" aria-hidden>
        <span className="dashboard-vocab-fill" style={{ width: `${pct}%` }} />
      </span>
      {inProgress > 0 && (
        <p className="dashboard-vocab-meta tabular-nums">
          {t("dashboardVocabInProgress").replace("{n}", String(inProgress))}
        </p>
      )}
      <p className="dashboard-viz-card-label mt-1">{t("dashboardVocabLearned")}</p>
    </>
  )
}

function QuickNav({ skillOrder, skillsByKey, focusKey, vocabulary, t }) {
  const total = vocabulary?.total || 0
  const mastered = vocabulary?.learned ?? vocabulary?.mastered ?? 0
  const toLearn = vocabulary?.to_learn ?? Math.max(0, total - mastered)
  const vocabPct = total > 0 ? Math.round((mastered / total) * 100) : 0
  return (
    <section className="dashboard-panel-3d dashboard-nav-section">
      <div className="dashboard-section-header">
        <div>
          <h2 className="dashboard-section-title">{t("dashboardQuickPractice")}</h2>
          <p className="dashboard-section-desc">{t("dashboardQuickPracticeHint")}</p>
        </div>
      </div>
      <div className="dashboard-nav-grid">
        {skillOrder.map((key) => {
          const skill = skillsByKey[key]
          const pct = skill ? Math.round(Math.min(skill.percentage, 100)) : 0
          const isFocus = key === focusKey
          return (
            <Link
              key={key}
              to={SKILL_ROUTES[key]}
              className={`dashboard-nav-card ${isFocus ? "dashboard-nav-card--focus" : ""}`}
              style={{ "--nav-accent": SKILL_COLORS[SKILL_KEYS.indexOf(key) % SKILL_COLORS.length] }}
            >
              <span className="dashboard-nav-card-name">{t(key)}</span>
              <span className="dashboard-nav-card-pct tabular-nums">{pct}%</span>
              <span className="dashboard-nav-card-bar" aria-hidden>
                <span className="dashboard-nav-card-bar-fill" style={{ width: `${pct}%` }} />
              </span>
              <span className="dashboard-nav-card-arrow" aria-hidden>
                →
              </span>
            </Link>
          )
        })}
        <Link
          to="/vocabulary"
          className={`dashboard-nav-card ${toLearn > 0 ? "dashboard-nav-card--focus" : ""}`}
          style={{ "--nav-accent": "var(--chart-5, var(--accent))" }}
        >
          <span className="dashboard-nav-card-name">{t("vocabulary")}</span>
          <span className="dashboard-nav-card-pct tabular-nums">
            {total > 0 ? `${mastered}/${total}` : t("dashboardVocabNoneSaved")}
          </span>
          <span className="dashboard-nav-card-bar" aria-hidden>
            <span className="dashboard-nav-card-bar-fill" style={{ width: `${vocabPct}%` }} />
          </span>
          <span className="dashboard-nav-card-arrow" aria-hidden>
            →
          </span>
        </Link>
      </div>
    </section>
  )
}

export default function Dashboard() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const location = useLocation()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [selectedDayDate, setSelectedDayDate] = useState(null)
  const shapesRef = useRef(null)

  const fetchDashboard = useCallback(() => {
    setLoading(true)
    setError("")
    return api
      .dashboard()
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (location.pathname !== "/dashboard") return
    let mounted = true
    setLoading(true)
    api
      .dashboard()
      .then((d) => mounted && setData(d))
      .catch((e) => mounted && setError(e.message))
      .finally(() => mounted && setLoading(false))
    return () => {
      mounted = false
    }
  }, [location.pathname])

  useEffect(() => {
    const onFocus = () => {
      if (location.pathname === "/dashboard") fetchDashboard()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && location.pathname === "/dashboard") {
        fetchDashboard()
      }
    }
    window.addEventListener("focus", onFocus)
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      window.removeEventListener("focus", onFocus)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [location.pathname, fetchDashboard])

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)")
    const onMove = (e) => {
      if (!mq.matches) return
      const layer = shapesRef.current
      if (!layer) return
      const x = (e.clientX / window.innerWidth - 0.5) * 2
      const y = (e.clientY / window.innerHeight - 0.5) * 2
      layer.style.transform = `rotateX(${y * -0.6}deg) rotateY(${x * 1.2}deg)`
    }
    window.addEventListener("mousemove", onMove)
    return () => window.removeEventListener("mousemove", onMove)
  }, [])

  const weekDays = useMemo(() => buildLast7Days(data?.daily_activity), [data?.daily_activity])

  useEffect(() => {
    if (!weekDays.length) return
    setSelectedDayDate((current) => current || weekDays[weekDays.length - 1].date)
  }, [weekDays])

  const skillsChartData = useMemo(
    () =>
      (data?.skills_progress || []).map((skill) => ({
        name: skill.skill_name,
        completed: skill.completed || 0,
        total: skill.total || 0,
        percentage: Math.min(skill.percentage || 0, 100),
        vocabulary_count: skill.vocabulary_count || 0,
      })),
    [data?.skills_progress]
  )

  const skillsByKey = useMemo(() => {
    const map = {}
    skillsChartData.forEach((s) => {
      map[s.name.toLowerCase()] = s
    })
    return map
  }, [skillsChartData])

  const focusArea = (data?.focus_area || "balanced").toLowerCase()
  const focusSkill = useMemo(
    () => resolveFocusSkill(focusArea, skillsChartData),
    [focusArea, skillsChartData]
  )
  const focusKey = (focusSkill || "").toLowerCase()
  const skillOrder = useMemo(() => orderedSkillKeys(focusSkill), [focusSkill])

  const strongestSkill = useMemo(() => {
    const withLessons = skillsChartData.filter((s) => s.total > 0)
    if (!withLessons.length) return null
    return [...withLessons].sort((a, b) => b.percentage - a.percentage)[0]?.name
  }, [skillsChartData])

  const totalLessons = useMemo(
    () => skillsChartData.reduce((sum, s) => sum + s.total, 0),
    [skillsChartData]
  )
  const completedLessons = data?.total_lessons_completed || 0
  const avgScore = typeof data?.average_score === "number" ? Math.round(data.average_score) : 0
  const streak = data?.learning_streak || 0
  const studyTime = formatStudyTime(data?.total_time_spent || 0)
  const todayMinutes = data?.today_time_spent || 0
  const dailyGoal = Math.max(data?.daily_goal_minutes || 30, 1)
  const dailyGoalPct = Math.min(Math.round((todayMinutes / dailyGoal) * 100), 100)
  const weekMaxLessons = Math.max(
    ...weekDays.map((d) => d.lessons_completed + d.vocabulary_reviewed),
    0
  )
  const weekLessonsTotal = weekDays.reduce((sum, d) => sum + d.lessons_completed, 0)
  const activeDays = weekDays.filter((d) => d.lessons_completed > 0 || d.total_activities > 0).length
  const overallProgress =
    totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0
  const vocab = data?.vocabulary || {}
  const vocabTotal = vocab.total || 0
  const vocabLearned = vocab.learned ?? vocab.mastered ?? 0
  const vocabInProgress = vocab.to_learn ?? Math.max(0, vocabTotal - vocabLearned)
  const isCustomFocus = focusArea !== "balanced"

  const sceneShell = (children) => (
    <div className="min-h-screen bg-background relative overflow-x-clip">
      <UniverseScene layerRef={shapesRef} />
      <div className="relative z-10 dashboard-scene">{children}</div>
    </div>
  )

  if (loading) {
    return sceneShell(
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 p-4">
        <div className="w-14 h-14 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        <p className="text-muted-foreground font-medium text-sm">{t("loadingCosmicJourney")}</p>
      </div>
    )
  }

  if (error) {
    return sceneShell(
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="dashboard-section-card p-8 max-w-md w-full text-center">
          <h3 className="text-lg font-semibold text-foreground mb-2">{t("somethingWentWrong")}</h3>
          <p className="text-muted-foreground mb-6 text-sm break-words">{error}</p>
          <button
            type="button"
            onClick={() => fetchDashboard()}
            className="button-glow bg-gradient-to-r from-primary to-accent text-primary-foreground px-6 py-3 rounded-xl font-medium text-sm"
          >
            {t("tryAgain")}
          </button>
        </div>
      </div>
    )
  }

  return sceneShell(
    <div className="dashboard-page max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-10 pb-10 sm:pb-12 w-full min-w-0">
      <header className="dashboard-hero-minimal mb-5 sm:mb-6">
        <div className="dashboard-hero-row">
          <div className="min-w-0">
            <p className="dashboard-hero-eyebrow">
              {t("welcomeBack")}
              {user?.username ? `, ${user.username}` : ""}
            </p>
            <h1 className="dashboard-hero-title">{t("dashboard")}</h1>
            <div className="dashboard-hero-accent" aria-hidden />
            <p className="dashboard-hero-subtitle">{t("dashboardSubtitle")}</p>
            {(data?.learning_goal || data?.english_level) && (
              <p className="dashboard-hero-meta">
                {data.english_level && (
                  <span className="dashboard-hero-chip">{data.english_level}</span>
                )}
                {data.learning_goal && (
                  <span className="dashboard-hero-goal">{data.learning_goal}</span>
                )}
              </p>
            )}
          </div>
          <div className="dashboard-hero-aside">
            <div
              className={`dashboard-streak-pill ${streak > 0 ? "dashboard-streak-pill--active" : ""}`}
            >
              <span className="dashboard-streak-pill-num tabular-nums">{streak}</span>
              <span className="dashboard-streak-pill-label">
                {streak > 0 ? t("dashboardStreakActive") : t("dashboardStreakStart")}
              </span>
            </div>
            <Link to="/settings" viewTransition className="dashboard-customize-link">
              {t("dashboardCustomize")}
            </Link>
          </div>
        </div>
      </header>

      {/* Daily goal + focus — productivity strip */}
      <section className="dashboard-prod-strip mb-5 sm:mb-6">
        <div className="dashboard-panel-3d dashboard-goal-card">
          <div className="dashboard-goal-head">
            <h2 className="dashboard-section-title">{t("dashboardDailyGoal")}</h2>
            <span className="tabular-nums text-sm text-muted-foreground">
              {todayMinutes}/{dailyGoal} {t("minutes")}
            </span>
          </div>
          <div className="dashboard-goal-track" aria-hidden>
            <div className="dashboard-goal-fill" style={{ width: `${dailyGoalPct}%` }} />
          </div>
          <p className="dashboard-section-desc mt-2">
            {dailyGoalPct >= 100
              ? t("dashboardDailyGoalMet")
              : t("dashboardDailyGoalHint").replace("{pct}", String(dailyGoalPct))}
            {vocabTotal > 0
              ? ` · ${t("vocabProgressHint")
                  .replace("{learned}", String(vocabLearned))
                  .replace("{total}", String(vocabTotal))}`
              : ""}
          </p>
        </div>

        {focusSkill ? (
          <Link to={skillRoute(focusSkill)} viewTransition className="dashboard-panel-3d dashboard-focus-banner">
            <div className="dashboard-focus-copy">
              <span className="dashboard-focus-eyebrow">
                {isCustomFocus ? t("dashboardYourFocus") : t("dashboardFocusArea")}
              </span>
              <span className="dashboard-focus-skill">{skillLabel(focusSkill, t)}</span>
              <span className="dashboard-focus-sub">
                {isCustomFocus
                  ? t("dashboardFocusFromSettings")
                  : t("dashboardFocusAutoWeakest")}
              </span>
            </div>
            <span className="dashboard-focus-cta">{t("dashboardPracticeNow")} →</span>
          </Link>
        ) : (
          <div className="dashboard-panel-3d dashboard-focus-banner dashboard-focus-banner--empty">
            <div className="dashboard-focus-copy">
              <span className="dashboard-focus-eyebrow">{t("dashboardFocusArea")}</span>
              <span className="dashboard-focus-skill">{t("dashboardPickFocus")}</span>
            </div>
            <Link to="/settings" viewTransition className="dashboard-focus-cta">
              {t("settings")} →
            </Link>
          </div>
        )}
      </section>

      <QuickNav
        skillOrder={skillOrder}
        skillsByKey={skillsByKey}
        focusKey={focusKey}
        vocabulary={vocab}
        t={t}
      />

      <div className="dashboard-kpi-grid mb-5 sm:mb-6 md:mb-8">
        <div className="dashboard-panel-3d dashboard-viz-card">
          <RingGauge
            value={avgScore}
            max={100}
            label={t("averageScore")}
            display={`${avgScore}%`}
          />
        </div>
        <div className="dashboard-panel-3d dashboard-viz-card">
          <RingGauge
            value={completedLessons}
            max={Math.max(totalLessons, 1)}
            label={t("lessonsCompleted")}
            display={completedLessons}
          />
        </div>
        <div className="dashboard-panel-3d dashboard-viz-card">
          <StreakViz days={weekDays} streak={streak} t={t} />
        </div>
        <div className="dashboard-panel-3d dashboard-viz-card dashboard-viz-card--time">
          <p className="dashboard-time-value tabular-nums">
            {studyTime.value}
            {studyTime.unit && <span className="dashboard-time-unit">{studyTime.unit}</span>}
          </p>
          <p className="dashboard-viz-card-label mt-1">{t("timeSpent")}</p>
        </div>
        <Link
          to="/vocabulary"
          viewTransition
          className="dashboard-panel-3d dashboard-viz-card dashboard-viz-card--vocab"
        >
          <VocabLearnedViz
            learned={vocabLearned}
            total={vocabTotal}
            inProgress={vocabInProgress}
            t={t}
          />
        </Link>
      </div>

      <div className="dashboard-charts-grid">
        <section className="dashboard-panel-3d dashboard-section-card min-w-0">
          <div className="dashboard-section-header">
            <div>
              <h2 className="dashboard-section-title">{t("dashboardWeeklyActivity")}</h2>
              <p className="dashboard-section-desc">{t("dashboardWeeklyActivityHint")}</p>
            </div>
            <div className="dashboard-section-stats">
              <span
                className="dashboard-section-stat tabular-nums"
                title={t("dashboardLessonsThisWeek")}
              >
                {weekLessonsTotal}
              </span>
              <span className="dashboard-section-stat-sub tabular-nums">
                {activeDays} {t("dashboardActiveDays")}
              </span>
            </div>
          </div>
          <div className="dashboard-week-scroll">
            <WeekChart
              days={weekDays}
              maxValue={weekMaxLessons}
              emptyLabel={t("dashboardNoWeeklyData")}
              selectedDate={selectedDayDate}
              onSelectDay={setSelectedDayDate}
              t={t}
            />
          </div>
        </section>

        <section className="dashboard-panel-3d dashboard-section-card min-w-0">
          <div className="dashboard-section-header">
            <div>
              <h2 className="dashboard-section-title">{t("skillsProgress")}</h2>
              {strongestSkill && (
                <p className="dashboard-section-desc">
                  {t("dashboardStrongestSkill")}: {skillLabel(strongestSkill, t)}
                </p>
              )}
            </div>
            <span
              className="dashboard-section-stat tabular-nums"
              title={t("dashboardOverallProgress")}
            >
              {overallProgress}%
            </span>
          </div>
          <SkillsChart skills={skillsChartData} focusName={focusSkill} t={t} />
        </section>
      </div>

      <nav className="dashboard-secondary-nav" aria-label={t("navigation")}>
        {[
          { key: "vocabulary", route: "/vocabulary" },
          { key: "grammar", route: "/grammar" },
          { key: "activities", route: "/activities" },
          { key: "profile", route: "/profile" },
          { key: "settings", route: "/settings" },
          { key: "submitSuggestion", route: "/feedback" },
        ].map((item) => (
          <Link key={item.key} to={item.route} viewTransition className="dashboard-secondary-link">
            {t(item.key)}
          </Link>
        ))}
      </nav>
    </div>
  )
}
